import { h } from '@shared/ui/dom.js';
import { sessao } from '@shared/sessao/sessao.js';
import { assinar, EVENTOS } from '@shared/lib/eventos.js';
import { marcaFutigol } from '@shared/ui/marca.js';
import { icone } from '@shared/ui/componentes/icone.js';
import { avatarUsuario } from '@shared/ui/componentes/avatar.js';
import { semInternet, estaOffline, podeUsarOffline } from '@shared/offline/conexao.js';
import { voltar, avancar, podeVoltar, podeAvancar } from '../roteador.js';
import '../estilos/layout.css';
import '../estilos/componentes.css';

/**
 * Casca do app: barra lateral (desktop), cabecalho (telefone/tablet), area de
 * conteudo onde o roteador pinta, e navegacao inferior (telefone/tablet).
 * Montada uma unica vez; so' o miolo troca.
 *
 * AS DUAS NAVEGACOES SAO SEMPRE RENDERIZADAS - quem escolhe qual aparece e' o
 * CSS, em 1024px. Sem listener de resize, sem re-render, sem piscada no
 * primeiro paint, sem handler orfao. O custo sao quatro <a> a mais no DOM, e em
 * troca `atualizarNav` continua sendo um percurso de DOM que nao sabe nada de
 * viewport.
 */
export function montarAppShell(raiz) {
    //A propria #app vira o grid da casca: evita um wrapper e evita mexer no
    //index.html.
    raiz.classList.add('casca');

    const saida = h('main', { class: 'conteudo', id: 'conteudo' });
    const lateral = barraLateral();
    const cab = cabecalho();
    const nav = navInferior();
    const faixa = faixaConexao();

    raiz.replaceChildren(lateral, cab, saida, nav, faixa);
    atualizarFaixaConexao(faixa);
    assinar(EVENTOS.CONEXAO_ALTERADA, () => atualizarFaixaConexao(faixa));

    //A navegacao mostra o item ativo e troca "Entrar"/"Eu" conforme a sessao.
    //O cabecalho entra na mesma lista porque ele tambem carrega o usuario -
    //`atualizarNav` percorre por seletor, nao por posicao, entao a peca que nao
    //tem nav simplesmente nao casa com nada.
    const partes = [lateral, nav, cab];
    assinar(EVENTOS.ROTA_ALTERADA, () => atualizarNav(partes));
    assinar(EVENTOS.SESSAO_ALTERADA, () => atualizarNav(partes));
    atualizarNav(partes);

    //Fora de `atualizarNav` porque a pergunta e' outra e o momento tambem: a nav
    //acende quando a rota TERMINA de carregar; o par voltar/avancar muda no
    //instante em que a pilha muda, que e' antes do chunk chegar.
    assinar(EVENTOS.HISTORICO_ALTERADO, () => atualizarHistorico(partes));
    atualizarHistorico(partes);

    return saida;
}

/**
 * O voltar/avancar do navegador, dentro do app.
 *
 * Existe porque a casca esconde a barra do navegador em duas situacoes que sao
 * a regra aqui, e nao a excecao: o app instalado (`display: standalone` no
 * manifest) nao TEM barra, e no telefone ela some assim que a pessoa rola. Sem
 * este par, desfazer um toque em tela cheia dependia do gesto do sistema - que
 * no iOS e' de borda e briga com o carrossel, e no Android as vezes fecha o app
 * inteiro.
 *
 * DESABILITADO E NAO ESCONDIDO nas pontas da pilha: o par muda de largura a
 * cada tela se um dos dois sumir, e o resto do cabecalho anda junto.
 */
function navegacaoHistorico(bloco) {
    return h('div', { class: `nav-historico ${bloco}__historico`, role: 'group', 'aria-label': 'Histórico de navegação' }, [h('button', { class: 'nav-historico__botao', type: 'button', title: 'Voltar', 'aria-label': 'Voltar para a tela anterior', dataset: { historico: 'voltar' }, onclick: voltar }, icone('seta-esquerda', { tamanho: 20 })), h('button', { class: 'nav-historico__botao', type: 'button', title: 'Avançar', 'aria-label': 'Avançar para a tela seguinte', dataset: { historico: 'avancar' }, onclick: avancar }, icone('seta-direita', { tamanho: 20 }))]);
}

function atualizarHistorico(partes) {
    const podem = { voltar: podeVoltar(), avancar: podeAvancar() };
    for (const raiz of partes) {
        for (const botao of raiz.querySelectorAll('[data-historico]')) botao.disabled = !podem[botao.dataset.historico];
    }
}

function cabecalho() {
    return h('header', { class: 'cabecalho' }, [
        h('a', { class: 'cabecalho__marca', href: '/', 'aria-label': 'Futigol, ir para o início' }, marcaFutigol({ altura: 24, titulo: null })),
        navegacaoHistorico('cabecalho'),
        h('div', { class: 'cabecalho__acoes' }, [
            h('button', { class: 'cabecalho__tema', type: 'button', title: 'Alternar tema', 'aria-label': 'Alternar tema claro e escuro', onclick: alternarTema }, icone('sol-lua', { tamanho: 20 })),
            //Vazio de proposito: quem pinta o retrato (ou o "Entrar") e'
            //`atualizarNav`, que ja' e' chamado no boot e a cada mudanca de
            //sessao. Um estado inicial aqui seria uma segunda verdade.
            h('a', { class: 'cabecalho__usuario', dataset: { usuario: '' } }),
        ]),
    ]);
}

/**
 * A faixa de conexao - o unico lugar da casca que fala do modo offline.
 *
 * E' `position: fixed` e nao mais um filho da grade de propósito: a `.casca` tem
 * duas linhas no telefone e duas colunas no desktop, e um quinto item em fluxo
 * empurraria o conteudo nos dois. Fixa, ela flutua acima da nav inferior e a
 * grade nem sabe que ela existe.
 *
 * NAO E' UM TOAST: ela fica enquanto o estado durar. "Sem conexao" nao e' um
 * aviso que passou - e' a condicao em que a pessoa esta' lendo tudo o que ve'.
 */
function faixaConexao() {
    return h('div', { class: 'faixa-conexao', role: 'status', hidden: true }, [h('span', { class: 'faixa-conexao__icone', 'aria-hidden': 'true' }, icone('alerta', { tamanho: 16 })), h('span', { class: 'faixa-conexao__texto' }), h('a', { class: 'faixa-conexao__acao', href: '/usuario/offline' }, 'Ver')]);
}

/**
 * Tres estados, e a diferenca entre eles importa:
 *
 *   sem internet, com conteudo ... "voce esta' vendo o que baixou"
 *   sem internet, sem recurso .... "as telas nao vao carregar" - a saida e' ligar
 *                                  o recurso e baixar, e o link leva la'
 *   offline por escolha .......... foi a pessoa que pediu; o tom e' outro
 */
function atualizarFaixaConexao(faixa) {
    const sem = semInternet();
    const offline = estaOffline();
    faixa.hidden = !sem && !offline;
    if (faixa.hidden) return;

    const porEscolha = offline && !sem;
    faixa.classList.toggle('faixa-conexao--escolha', porEscolha);
    faixa.querySelector('.faixa-conexao__texto').textContent = porEscolha ? 'Trabalhando offline por sua escolha' : podeUsarOffline() ? 'Sem conexão — mostrando o conteúdo do aparelho' : 'Sem conexão com a internet';
    faixa.querySelector('.faixa-conexao__acao').textContent = porEscolha || podeUsarOffline() ? 'Ver' : 'Trabalhar offline';
}

//`naInferior` existe porque a nav inferior comporta 4 alvos de toque
//confortaveis e a barra lateral comporta mais - a lateral leva a lista inteira,
//e o telefone leva os quatro destinos mais procurados.
//
//"Eu" fica de fora do telefone de proposito: o retrato do cabecalho ja' e' esse
//mesmo alvo, e repeti-lo custaria uma das quatro vagas.
//
//Classificacao e Jogos sao atalhos DENTRO da competicao selecionada - e' a
//guarda `exigirCompeticao` que manda escolher uma quando ainda nao ha'.
//`casaCom` sao os enderecos irmaos que acendem o mesmo item: os tres recortes
//de jogos sao uma secao so' para quem navega.
const ITENS_NAV = [
    { rotulo: 'Início', href: '/', icone: 'casa', naInferior: true },
    { rotulo: 'Competições', href: '/competicoes', icone: 'trofeu', naInferior: true },
    { rotulo: 'Classificação', href: '/competicoes/classificacao', icone: 'tabela' },
    { rotulo: 'Jogos', href: '/competicoes/jogos', icone: 'bola', casaCom: ['/competicoes/jogos-marcados', '/competicoes/jogos-realizados'], naInferior: true },
    //Logo abaixo de Jogos porque e' a MESMA agenda por outro recorte: as tres
    //telas de jogos sao listas de uma ponta do tempo cada, e o calendario e' o
    //unico lugar em que o jogado e o por jogar convivem na mesma grade. Fica
    //fora da nav inferior - as quatro vagas do telefone ja' estao ocupadas, e
    //uma grade de mes e' leitura de tela grande.
    { rotulo: 'Calendário', href: '/competicoes/calendario', icone: 'calendario' },
    { rotulo: 'Clubes', href: '/clubes', icone: 'escudo-clube' },
    { rotulo: 'Favoritos', href: '/usuario/favoritos', icone: 'estrela', naInferior: true },
    { rotulo: 'Eu', href: '/usuario/perfil', icone: 'pessoa' },
];

/**
 * Um unico construtor alimenta as duas navegacoes - e' o que garante que elas
 * nunca divirjam. O bloco BEM viaja no dataset para `atualizarNav` saber qual
 * modificador aplicar sem consultar o viewport.
 */
function itemNav(item, bloco) {
    return h('a', { class: `${bloco}__item`, href: item.href, dataset: { href: item.href, bloco, ...(item.casaCom ? { casaCom: item.casaCom.join(' ') } : {}) } }, [h('span', { class: `${bloco}__icone` }, icone(item.icone, { tamanho: 22 })), h('span', { class: `${bloco}__rotulo` }, item.rotulo)]);
}

function navInferior() {
    return h(
        'nav',
        { class: 'nav-inferior', 'aria-label': 'Navegação principal' },
        ITENS_NAV.filter((item) => item.naInferior).map((item) => itemNav(item, 'nav-inferior'))
    );
}

function barraLateral() {
    return h('aside', { class: 'barra-lateral' }, [
        h('a', { class: 'barra-lateral__marca', href: '/', 'aria-label': 'Futigol, ir para o início' }, marcaFutigol({ altura: 30, titulo: null })),
        navegacaoHistorico('barra-lateral'),
        h(
            'nav',
            { class: 'barra-lateral__nav', 'aria-label': 'Navegação lateral' },
            ITENS_NAV.map((item) => itemNav(item, 'barra-lateral'))
        ),
        h('button', { class: 'barra-lateral__tema', type: 'button', 'aria-label': 'Alternar tema claro e escuro', onclick: alternarTema }, [icone('sol-lua', { tamanho: 20 }), h('span', {}, 'Tema')]),
    ]);
}

//'/' so' casa exato; o resto casa por prefixo, para a sub-rota manter o item
//aceso (/competicoes/rodadas continua em "Competições").
function casa(padrao, caminho) {
    if (padrao === '/') return caminho === '/';
    return caminho === padrao || caminho.startsWith(padrao + '/');
}

//O comprimento do padrao que casou E' a especificidade dele (0 = nao casou):
//entre /competicoes e /competicoes/classificacao ganha o mais longo, que e'
//justamente o item mais proximo de onde a pessoa esta'.
function especificidade(link, caminho) {
    const { href, casaCom } = link.dataset;
    const padroes = casaCom ? [href, ...casaCom.split(' ')] : [href];
    return padroes.reduce((maior, padrao) => (casa(padrao, caminho) ? Math.max(maior, padrao.length) : maior), 0);
}

function atualizarNav(partes) {
    const caminho = location.pathname;
    const usuario = sessao.usuario;

    for (const raiz of partes) {
        //UM SO' item acende: quem tiver o padrao MAIS LONGO entre os que casaram.
        //Sem isso /competicoes/jogos acenderia "Jogos" e "Competições" juntos -
        //dois "voce' esta' aqui" ao mesmo tempo.
        const links = [...raiz.querySelectorAll('[data-href]')];
        const pontos = links.map((link) => especificidade(link, caminho));
        const melhor = Math.max(0, ...pontos);

        links.forEach((link, i) => {
            const ativo = pontos[i] > 0 && pontos[i] === melhor;
            link.classList.toggle(`${link.dataset.bloco}__item--ativo`, ativo);
            if (ativo) link.setAttribute('aria-current', 'page');
            else link.removeAttribute('aria-current');
        });

        //Retrato do cabecalho (telefone/tablet): logado e' o disco com as
        //iniciais, deslogado e' a pilula "Entrar" - o mesmo alvo, dois estados.
        const alvoUsuario = raiz.querySelector('[data-usuario]');
        if (alvoUsuario) {
            alvoUsuario.setAttribute('href', usuario ? '/usuario/perfil' : '/usuario/entrar');
            alvoUsuario.setAttribute('aria-label', usuario ? `Meu perfil, ${usuario.Nome ?? ''}`.trim() : 'Entrar na conta');
            alvoUsuario.classList.toggle('cabecalho__usuario--avatar', !!usuario);
            //O usuario INTEIRO, e nao o nome: e' o objeto que carrega `Imagem`, e
            //e' o que faz o retrato ser a foto de quem esta' logado quando ela
            //existe. Ver ui/componentes/avatar.js.
            alvoUsuario.replaceChildren(usuario ? avatarUsuario(usuario, { tamanho: 32 }) : document.createTextNode('Entrar'));
        }

        //O item guarda data-href="/usuario/perfil" enquanto o href alterna para
        ///usuario/entrar: e' por isso que o seletor acima le data-href, nunca
        //href.
        const item = raiz.querySelector('[data-href="/usuario/perfil"]');
        if (!item) continue;
        item.setAttribute('href', usuario ? '/usuario/perfil' : '/usuario/entrar');
        const rotulo = item.querySelector(`.${item.dataset.bloco}__rotulo`);
        if (rotulo) rotulo.textContent = usuario ? 'Eu' : 'Entrar';
        //Logado, o icone generico de pessoa da' lugar ao retrato: e' o que faz a
        //barra lateral e a nav inferior dizerem QUEM esta' logado, e nao so' que
        //existe uma area de usuario.
        const iconeItem = item.querySelector(`.${item.dataset.bloco}__icone`);
        if (iconeItem) iconeItem.replaceChildren(usuario ? avatarUsuario(usuario, { tamanho: 24 }) : icone('pessoa', { tamanho: 22 }));
    }
}

const CHAVE_TEMA = 'futigol.tema';

function alternarTema() {
    const atual = document.documentElement.getAttribute('data-tema');
    const escuroDoSistema = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const proximo = atual ? (atual === 'escuro' ? 'claro' : 'escuro') : escuroDoSistema ? 'claro' : 'escuro';
    document.documentElement.setAttribute('data-tema', proximo);
    try {
        localStorage.setItem(CHAVE_TEMA, proximo);
    } catch {
        /* modo privativo */
    }
}

export function aplicarTemaSalvo() {
    try {
        const salvo = localStorage.getItem(CHAVE_TEMA);
        if (salvo) document.documentElement.setAttribute('data-tema', salvo);
    } catch {
        /* modo privativo */
    }
}
